import { useCallback, useEffect, useState } from 'react';
import { checkToken } from '../api';
import { readHashToken, clearHash } from '../utils/emailLinks';

// Reads the token from an emailed link's #token=..., takes it off the
// address bar, and asks the server whether it is still good for `purpose`
// ('invite' or 'reset').
//
// status: 'checking' | 'valid' | 'invalid' | 'failed' (could not reach the server)
export default function useEmailLinkToken(purpose) {
  const [token] = useState(() => readHashToken());
  const [status, setStatus] = useState(token ? 'checking' : 'invalid');
  const [details, setDetails] = useState(null);
  const [error, setError] = useState(token ? '' : 'This link is incomplete. Open it again from the email, or ask for a new one.');

  useEffect(() => { clearHash(); }, []);

  const check = useCallback(async () => {
    if (!token) return;
    setStatus('checking');
    setError('');
    try {
      const res = await checkToken(token, purpose);
      if (res.data?.valid) {
        setDetails(res.data);
        setStatus('valid');
      } else {
        setError(res.data?.error || 'This link is invalid or has expired.');
        setStatus('invalid');
      }
    } catch (err) {
      if (err.response) {
        setError(err.response.data?.error || 'This link is invalid or has expired.');
        setStatus('invalid');
      } else {
        setError('Could not check this link. Check your connection and try again.');
        setStatus('failed');
      }
    }
  }, [token, purpose]);

  useEffect(() => { check(); }, [check]);

  return { token, status, details, error, retry: check };
}
